/**
 * multiAgentArtifactOverlayStore — F12: the "layover" for chat artifacts.
 *
 * A single, app-wide overlay target so any artifact surface (ArtifactCard,
 * CitationChip, the Data-results section) can pop the SAME centered modal that
 * shows the artifact's code full-size with a close — mirroring the way the
 * session/history symbol opens a layover. Keeping it in one store avoids
 * prop-drilling an onPreview callback through ChatPanel → ResultColumn →
 * ArtifactCard → CitationChip.
 */
import { create } from "zustand";
import { registerOrgScopedReset } from "@/lib/org-scope";

/** The fields ArtifactCard needs — kept structurally identical to ArtifactCard's
 *  `artifact` prop so a card can hand its own `a` straight to `open()` and the
 *  overlay can hand it straight back, with no null/undefined intake friction. */
export interface OverlayArtifact {
  id: string;
  type: string;
  name: string;
  toolName: string;
  byteSize: number;
  createdAt: number;
  intake?: Record<string, unknown> | null;
}

interface ArtifactOverlayState {
  /** The artifact currently shown in the layover, or null when closed. */
  artifact: OverlayArtifact | null;
  open: (artifact: OverlayArtifact) => void;
  close: () => void;
}

export const useArtifactOverlayStore = create<ArtifactOverlayState>((set) => ({
  artifact: null,
  open: (artifact) => set({ artifact }),
  close: () => set({ artifact: null }),
}));

// ── Organization scoping ─────────────────────────────────────────────────────
//
// The overlay holds an artifact produced by a run in a specific organization —
// code, a diff, an export. Leaving it open across a switch would show one
// organization's generated output on top of another's workspace.
registerOrgScopedReset("multiAgentArtifactOverlayStore", () => {
  useArtifactOverlayStore.setState({ artifact: null });
});
